import type { Result } from '@/classes/result';
import { ErrorMessage } from '@/messages/error';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';
import { isISODate } from '@/utils/date';
import axios, { AxiosError, type AxiosResponse } from 'axios';

function jsonTransformer(data: any): any {
    if (data === '' || data == null) return null;
    return JSON.parse(data);
}

function dateTransformer(data: any): any {
    if (!data) return data;

    if (isISODate(data)) {
        return new Date(data);
    }
    if (Array.isArray(data)) {
        return data.map((val: any) => dateTransformer(val));
    }
    if (typeof data === 'object') {
        return Object.fromEntries(Object.entries(data).map(([key, val]) => [key, dateTransformer(val)]));
    }
    return data;
}

/** Interceptor for when server responds with 2xx status code */
function successResponseInterceptor(response: AxiosResponse): AxiosResponse {
    const globalStore = useGlobalStore();
    globalStore.isConnectionError = false;
    globalStore.isLoading = false;

    return response;
}

/** Interceptor for when server responds with anything but 2xx status code */
function errorResponseInterceptor(error: AxiosError): Promise<never | AxiosResponse> | undefined {
    const globalStore = useGlobalStore();
    globalStore.isLoading = false;

    if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.code === 'ERR_FAILED') {
        globalStore.isConnectionError = true;
        return;
    }
    globalStore.isConnectionError = false;

    if (error.response) {
        const response: AxiosResponse = error.response as AxiosResponse;
        const result = response.data as Result;

        if (response.status === 403) {
            if (result.message === ErrorMessage.NOT_AUTHORISED_ACCOUNT_INACTIVE) {
                const authStore = useAuthStore();
                if (authStore.isAuthenticated) authStore.logout();
                return Promise.resolve(response);
            }

            if (result.message === ErrorMessage.INVALID_TOKEN) {
                router.push({ name: 'login' });
            } else {
                router.push({ name: 'error', params: { statusCode: response.status } });
            }

            return Promise.resolve(response);
        }
    }

    return Promise.reject(error);
}

export default {
    install: () => {
        axios.interceptors.response.use(successResponseInterceptor, errorResponseInterceptor);
        axios.defaults.transformResponse = [jsonTransformer, dateTransformer];
    }
};
